import React, {useMemo} from 'react';
import {type AutocompleteValue} from '@mui/material';
import type {AttributeSetId} from '@netx/core-attribute-set';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useAttributeSetSelectionProvider} from '../../context';
import {
	SetSelector as BaseSetSelector,
	type SetSelectorProps,
} from './SetSelector';

export * from '@netx/core-components/SelectField/selectFieldClasses';

export interface SelectionProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends Partial<
	Omit<
		SetSelectorProps<Multiple, DisableClearable>,
		'attributeSets' | 'enforceAttributeSets' | 'value'
	>
> {}

export interface Option {
	group: string;
	id: AttributeSetId;
	active?: 0 | 1;
	name: string;
}

export const SetSelectorPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		label,
		multiple,
		onChange,
		...props
	}: SelectionProps<Multiple, DisableClearable>,
	ref: any,
) => {
	const attributeSetSelection = useAttributeSetSelectionProvider();

	const attributeSets = useMemo(
		() => attributeSetSelection.attributeSetCtx.getSelectable(),
		[attributeSetSelection.attributeSetCtx.data],
	);

	const handleSelectionChange = useConstCallback(
		(event: any, value: any, reason: any, details: any) => {
			const pureValue = value;
			if (!Array.isArray(value)) value = [value];

			value =
				value == null
					? []
					: value.map((val?: Option) => (val?.id ? val.id : val));

			if (value.join() === attributeSetSelection.selected.join()) return;

			attributeSetSelection.setSelected(value);
			onChange?.(event, pureValue, reason, details);
		},
	);

	const value = attributeSetSelection.selected;

	const ourValue = (multiple ? value : value[0] || null) as AutocompleteValue<
		string,
		Multiple,
		DisableClearable,
		false
	>;

	return (
		<BaseSetSelector<Multiple, DisableClearable>
			ref={ref}
			attributeSets={attributeSets}
			enforceAttributeSets={attributeSetSelection.enforce}
			multiple={multiple}
			{...props}
			onChange={handleSelectionChange}
			value={ourValue}
		/>
	);
};

export const SetSelector = React.forwardRef(SetSelectorPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: SelectionProps<Multiple, DisableClearable>,
) => JSX.Element;

export default SetSelector;
